
import { defineComponent, ref, onMounted, computed } from "vue"
import { Actions } from "@/store/enums/StoreEnums"
import { useStore } from "vuex"
import { useRoute, useRouter } from "vue-router"
import Swal from "sweetalert2/dist/sweetalert2.min.js"
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb"
import type { ElForm } from "element-plus"
import { Plus, Delete } from "@element-plus/icons-vue"
import type { ElUpload, ElInput } from "element-plus"
import { Product, ProductGift } from "@/store/modules/ProductModule"
import { BoxType } from "@/store/modules/BoxInfoModule"
import NewBoxItemInfoModal from "@/components/modals/forms/NewBoxItemInfoModal.vue"
import * as _ from 'lodash'

type FormInstance = InstanceType<typeof ElForm>;

export default defineComponent({
  components: {
    Plus, 
    Delete,
    NewBoxItemInfoModal
  },
  setup() {
    onMounted(() => {
      if (!route.params.id) {
        setCurrentPageBreadcrumbs("商品新增", ["商品管理;products"])
      } else {
        setCurrentPageBreadcrumbs("商品编辑", ["商品管理;products", route.params.id.toString()])
      }
    });

    const rules = ref({
      image: [
        {
          validator: (rule: any, value: any, callback: any) => {
            if (!imageUrl.value && !product.value?.image) {
              callback(new Error('请上传商品图片'))
              return
            }
            callback()
          },
          trigger: 'change'
        }
      ],
      name: [
        { required: true, message: '名称不可为空', trigger: 'blur' },
        { min: 1, max: 200, message: '名称长度需在 1-200 之间', trigger: 'blur' }
      ],
      product_type: [
        { required: true, message: '请选择商品类型', trigger: 'change' }
      ],
      recycle_type: [
        {
          validator: (rule: any, value: any, callback: any) => {
            if (product.value?.product_type === 'reward_pack') {
              if (!['point', 'membership_point'].includes(value)) {
                callback(new Error('奖励包仅支持 point 或 membership_point'))
                return
              }
            }
            if (!value) { callback(new Error('请选择兑换类型')); return }
            callback()
          },
          trigger: 'change'
        }
      ],
      price: [
        {
          validator: (rule: any, value: any, callback: any) => {
            if (value === undefined || value === null) { callback(); return }
            const v = Number(value)
            if (isNaN(v)) { callback(new Error('市场价需为数字')); return }
            if (v < 0) { callback(new Error('市场价需≥0')); return }
            callback()
          },
          trigger: ['blur','change']
        }
      ],
      recycle_price: [
        {
          validator: (rule: any, value: any, callback: any) => {
            const v = Number(value)
            if (isNaN(v)) { callback(new Error('回收价需为数字')); return }
            if (v < 0) { callback(new Error('回收价需≥0')); return }
            callback()
          },
          trigger: ['blur','change']
        }
      ],
      stock: [
        {
          validator: (rule: any, value: any, callback: any) => {
            if (value === undefined || value === null) { callback(); return }
            const v = Number(value)
            if (!Number.isInteger(v)) { callback(new Error('库存需为整数')); return }
            if (v < 0) { callback(new Error('库存需≥0')); return }
            callback()
          },
          trigger: ['blur','change']
        }
      ],
      pool_type: [
        {
          validator: (rule: any, value: any, callback: any) => {
            if (product.value?.product_type === 'box_infinity' && !value) {
              callback(new Error('无限盒中盒需选择商品池类型'))
              return
            }
            callback()
          },
          trigger: 'change'
        }
      ],
      event_point: [
        {
          validator: (rule: any, value: any, callback: any) => {
            if (product.value?.event_id === '' || product.value?.event_id === null || product.value?.event_id === undefined) { callback(); return }
            const v = Number(value)
            if (isNaN(v)) { callback(new Error('活动点数需为数字')); return }
            if (v <= 0) { callback(new Error('活动点数需>0')); return }
            callback()
          },
          trigger: ['blur','change']
        }
      ],
      reward_min: [
        {
          validator: (rule: any, value: any, callback: any) => {
            if (product.value?.product_type !== 'reward_pack') { callback(); return }
            const min = Number(value)
            const max = Number(product.value?.reward_max)
            if (isNaN(min)) { callback(new Error('请填写奖励最小值')); return }
            if (min < 0) { callback(new Error('最小值需为非负数')); return }
            if (!isNaN(max) && min > max) { callback(new Error('最小值不得大于最大值')); return }
            callback()
          },
          trigger: ['blur','change']
        }
      ],
      reward_max: [
        {
          validator: (rule: any, value: any, callback: any) => {
            if (product.value?.product_type !== 'reward_pack') { callback(); return }
            const max = Number(value)
            const min = Number(product.value?.reward_min)
            if (isNaN(max)) { callback(new Error('请填写奖励最大值')); return }
            if (max <= 0) { callback(new Error('最大值需大于 0')); return }
            if (!isNaN(min) && min > max) { callback(new Error('最小值不得大于最大值')); return }
            callback()
          },
          trigger: ['blur','change']
        }
      ],
      product_gifts_attributes: [
        {
          validator: (rule: any, _value: any, callback: any) => {
            if (!(product.value?.product_type === 'box_normal' || product.value?.product_type === 'box_infinity')) { callback(); return }
            const items = (product.value?.product_gifts_attributes || []).filter((i: any) => i && i._destroy != 1)
            if (items.length === 0) { callback(new Error('请至少添加一个盒中盒赏品')); return }
            for (const it of items) {
              if (!it.gift_product_id) { callback(new Error('赏品条目不完整：缺少商品')); return }
              if (!(Number(it.quantity) > 0)) { callback(new Error('赏品数量需>0')); return }
            }
            callback()
          },
          trigger: 'change'
        }
      ],
      // 商城相关验证规则
      mall_stock: [
        {
          validator: (rule: any, value: any, callback: any) => {
            if (!product.value?.is_mall_product) { callback(); return }
            if (value === undefined || value === null || value < 0) {
              callback(new Error('商城库存需≥0'))
              return
            }
            callback()
          },
          trigger: ['blur', 'change']
        }
      ],
      deposit_amount: [
        {
          validator: (rule: any, value: any, callback: any) => {
            if (!product.value?.is_mall_product || !product.value?.is_presale) { callback(); return }
            if (product.value?.presale_payment_mode === 'full_only') { callback(); return }
            if (product.value?.deposit_type !== 'fixed') { callback(); return }
            if (value === undefined || value === null || value <= 0) {
              callback(new Error('订金金额需>0'))
              return
            }
            callback()
          },
          trigger: ['blur', 'change']
        }
      ],
      deposit_percent: [
        {
          validator: (rule: any, value: any, callback: any) => {
            if (!product.value?.is_mall_product || !product.value?.is_presale) { callback(); return }
            if (product.value?.presale_payment_mode === 'full_only') { callback(); return }
            if (product.value?.deposit_type !== 'percent') { callback(); return }
            if (value === undefined || value === null || value < 1 || value > 100) {
              callback(new Error('订金比例需在 1-100 之间'))
              return
            }
            callback()
          },
          trigger: ['blur', 'change']
        }
      ]
    });

    const formRef = ref<FormInstance>()
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const product = ref({} as Product)

    const onCancel = () => {
      router.push({ name: "products" });
    };

    const isNew = !route.params.id;

    // 从商城商品页点「新增商城商品」进来时，预先打开「商城商品」开关
    if (isNew && route.query.is_mall_product === "true") {
      product.value.is_mall_product = true;
    }

    const onSubmit = (formEl: FormInstance | undefined) => {
      if (!formEl) return;

      formEl.validate((valid) => {
        if (valid) {
          product.value.product_images_attributes = submitFileList

          if (isNew) {
            store
              .dispatch(
                Actions.CREATE_PRODUCT,
                product.value
              )
              .then(() => {
                router.push({ name: "products" });
              })
              .catch(() => {
                // const [error] = Object.keys(store.getters.getErrors);
                // Swal.fire({
                //   text: store.getters.getErrors[error],
                //   icon: "error",
                //   buttonsStyling: false,
                //   confirmButtonText: "Try again!",
                //   customClass: {
                //     confirmButton: "btn fw-bold btn-light-danger",
                //   },
                // });
              });
          } else {
            store
              .dispatch(Actions.UPDATE_PRODUCT, {
                id: route.params.id,
                values: product.value,
              })
              .then(() => {
                router.push({ name: "products" });
              })
              .catch(() => {
                // const [error] = Object.keys(store.getters.getErrors);
                // Swal.fire({
                //   text: store.getters.getErrors[error],
                //   icon: "error",
                //   buttonsStyling: false,
                //   confirmButtonText: "Try again!",
                //   customClass: {
                //     confirmButton: "btn fw-bold btn-light-danger",
                //   },
                // });
              });
          }
        } else {
          return false;
        }
      });
    };

    if (!isNew) {
      store
        .dispatch(Actions.GET_PRODUCT, route.params.id)
        .then(() => {
          product.value = store.getters.currentProduct

          imageUrl.value = product.value.image_url.startsWith("http")
            ? product.value.image_url
            : ""

          product.value.product_images_attributes.forEach(function (i) {
            fileList.value.push({
              id: i.id,
              url: i.image.url,
            })
          })
        })
        .catch(() => {
          // const [error] = Object.keys(store.getters.getErrors);
          // Swal.fire({
          //   text: store.getters.getErrors[error],
          //   icon: "error",
          //   buttonsStyling: false,
          //   confirmButtonText: "Try again!",
          //   customClass: {
          //     confirmButton: "btn fw-bold btn-light-danger",
          //   },
          // });
        });
    } else {
      product.value.product_gifts_attributes = []
      product.value.product_type = 'normal'
    }

    const imageUrl = ref("")

    const onChange = (file) => {
      var reader = new FileReader();
      reader.readAsDataURL(file.raw)
      reader.onload = (e) => {
        imageUrl.value = e.target?.result?.toString() || ""
        product.value.image = imageUrl.value
      }
    }

    const product_brand_options = ref(Array<BoxType>())

    store
      .dispatch(Actions.GET_PRODUCT_BRAND_OPTIONS)
      .then(() => {
        product_brand_options.value = product_brand_options.value.concat(store.getters.currentProductBrandOptions)
      })
      .catch(() => {
        // const [error] = Object.keys(store.getters.getErrors);
        // Swal.fire({
        //   text: store.getters.getErrors[error],
        //   icon: "error",
        //   buttonsStyling: false,
        //   confirmButtonText: "Try again!",
        //   customClass: {
        //     confirmButton: "btn fw-bold btn-light-danger",
        //   },
        // });
      })

    const product_recycle_type_options = ref([] as any)

    store
      .dispatch(Actions.GET_PRODUCT_RECYCLE_TYPE_OPTIONS)
      .then(() => {
        product_recycle_type_options.value = product_recycle_type_options.value.concat(store.getters.currentProductRecycleTypeOptions)
      })
      .catch(() => {
        // const [error] = Object.keys(store.getters.getErrors);
        // Swal.fire({
        //   text: store.getters.getErrors[error],
        //   icon: "error",
        //   buttonsStyling: false,
        //   confirmButtonText: "Try again!",
        //   customClass: {
        //     confirmButton: "btn fw-bold btn-light-danger",
        //   },
        // });
      })

    const product_ip_options = ref([] as any)

    store
      .dispatch(Actions.GET_PRODUCT_IP_OPTIONS)
      .then(() => {
        product_ip_options.value = product_ip_options.value.concat(store.getters.currentProductIpOptions)
      })
      .catch(() => {
        // const [error] = Object.keys(store.getters.getErrors);
        // Swal.fire({
        //   text: store.getters.getErrors[error],
        //   icon: "error",
        //   buttonsStyling: false,
        //   confirmButtonText: "Try again!",
        //   customClass: {
        //     confirmButton: "btn fw-bold btn-light-danger",
        //   },
        // });
      })

    const event_options = ref([] as any)

    store
      .dispatch(Actions.GET_EVENTS)
      .then(() => {
        event_options.value = [{id: '', name: ''} as any]
        event_options.value = event_options.value.concat(store.getters.currentEvents)
      })
      .catch(() => {
        // const [error] = Object.keys(store.getters.getErrors);
        // Swal.fire({
        //   text: store.getters.getErrors[error],
        //   icon: "error",
        //   buttonsStyling: false,
        //   confirmButtonText: "Try again!",
        //   customClass: {
        //     confirmButton: "btn fw-bold btn-light-danger",
        //   },
        // });
      })

    const fileList = ref([] as any);
    const submitFileList = [] as any;

    const imageListOnChange = (file, fileList) => {
      var reader = new FileReader();
      reader.readAsDataURL(file.raw);
      reader.onload = (e) => {
        submitFileList.push({
          image: e.target?.result,
        })
      }
    }

    const handleRemove = (file) => {
      if (file.id) {
        submitFileList.push({
          id: file.id,
          _destroy: 1,
        })
      }

      let resultArr = fileList.value.filter((i) => {
        return i.id === file.id && i.url === file.url;
      })

      fileList.value.splice(fileList.value.indexOf(resultArr[0]), 1);
    }

    const orderedProductGifts = () => {
      return _.orderBy(product.value.product_gifts_attributes, 'sorting', 'asc')
    }

    const move_up = (item) => {
      if (item.sorting > 0) {
        item.sorting -= 1
      }

      let sorting_items = _.orderBy(product.value.product_gifts_attributes, 'sorting', 'asc').filter((i) => {
        return i != item
      })

      for(var i = 0; i < sorting_items.length; i++) {
        if (i >= item.sorting) {
          sorting_items[i].sorting = i + 1
        } else {
          sorting_items[i].sorting = i
        }
      }
    }

    const move_down = (item) => {
      if (item.sorting < product.value.product_gifts_attributes.length - 1) {
        item.sorting += 1
      }

      let sorting_items = _.orderBy(product.value.product_gifts_attributes, 'sorting', 'asc').filter((i) => {
        return i != item
      })

      for(var i = 0; i < sorting_items.length; i++) {
        if (i >= item.sorting) {
          sorting_items[i].sorting = i + 1
        } else {
          sorting_items[i].sorting = i
        }
      }
    }

    const add_box_item_infos = (products) => {
      products.forEach((i) => {
        product.value.product_gifts_attributes.push({
          gift_product_id: i.id,
          brand: i.brand,
          product: i.name,
          price: i.price,
          recycle_price: i.recycle_price,
          quantity: 1,
          sorting: product.value.product_gifts_attributes.length
        } as ProductGift)
      })
    }

    const destroy_item = (item) => {
      item._destroy = 1
    }

    const avarage_price = () => {
      if (!(product.value && product.value.product_gifts_attributes)) {
        return 0
      }

      let total = 0
      let quantity = 0

      product.value.product_gifts_attributes.forEach((i) => {
        total += i.recycle_price * i.quantity
        quantity += i.quantity
      })

      if (quantity == 0) {
        return 0
      } else {
        return (total / quantity).toFixed(1)
      }
    }

    const sortByPrice = () => {
      const items = product.value.product_gifts_attributes
      const sorted = _.orderBy(items, (i) => Number(i.recycle_price) || 0, 'desc')
      sorted.forEach((item, index) => {
        item.sorting = index
      })
      // 重新赋值数组触发 Vue 响应式更新
      product.value.product_gifts_attributes = [...items]
    }

    const total_quantity = () => {
      if (!(product.value && product.value.product_gifts_attributes)) {
        return 0
      }

      let total = 0

      product.value.product_gifts_attributes.forEach((i: ProductGift) => {
        if (!i._destroy) {
          total += i.quantity
        }
      })

      return total
    }

    const filteredRecycleTypeOptions = computed(() => {
      if (product.value && product.value.product_type === 'reward_pack') {
        return product_recycle_type_options.value.filter((i: any) => ['point', 'membership_point'].includes(i.value))
      }
      return product_recycle_type_options.value
    })

    // 计算订金金额（百分比模式）
    const calculatedDeposit = computed(() => {
      if (!product.value?.price || !product.value?.deposit_percent) {
        return '0.00'
      }
      const deposit = (Number(product.value.price) * Number(product.value.deposit_percent) / 100)
      return deposit.toFixed(2)
    })

    const formatPrice = (price: number | string | null | undefined): string => {
      if (price === null || price === undefined) return '0.0'
      const num = Number(price)
      return isNaN(num) ? '0.0' : num.toFixed(1)
    }

    return {
      onSubmit,
      product,
      rules,
      onCancel,
      formRef,
      imageUrl,
      onChange,
      product_brand_options,
      product_ip_options,
      product_recycle_type_options,
      imageListOnChange,
      fileList,
      handleRemove,
      event_options,
      orderedProductGifts,
      move_up,
      move_down,
      add_box_item_infos,
      destroy_item,
      avarage_price,
      sortByPrice,
      total_quantity,
      filteredRecycleTypeOptions,
      calculatedDeposit,
      formatPrice
    }
  }
});
